import React, { useState } from 'react'
import { BarChart3, Download, AlertCircle, FileSpreadsheet, FileText, Search } from 'lucide-react'
import { apiService } from '../services/apiService'
import { exportCumulativeReportToExcel, exportCumulativeReportToCsv } from '../utils/statementUtils'
import { generateCumulativeReportPDF } from '../utils/pdfGenerator'

const fmt = (n) =>
  new Intl.NumberFormat('en-UG', { style: 'currency', currency: 'UGX', maximumFractionDigits: 0 }).format(n || 0)

// Cumulative financial report over a date range. Customers see their own
// figures; admins pass `isAdmin` and either a fixed `memberNo` or let the
// panel show a member-number input.
const CumulativeReportPanel = ({ isAdmin = false, memberNo = null, customerName = '', companyName = 'UFCC Microfinance' }) => {
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [memberInput, setMemberInput] = useState(memberNo || '')
  const [report, setReport] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [exporting, setExporting] = useState('')

  const effectiveMemberNo = memberNo || memberInput.trim()
  const memberLabel = customerName || effectiveMemberNo

  const generate = async () => {
    if (isAdmin && !effectiveMemberNo) {
      setError('Enter a member number to generate the report.')
      return
    }
    setLoading(true)
    setError('')
    try {
      const res = await apiService.getCumulativeFinancialReport(
        { startDate, endDate, memberNo: isAdmin ? effectiveMemberNo : null },
        isAdmin
      )
      if (res.success || res.status === 'SUCCESS') {
        setReport(res.data)
        if (!(res.data?.profit_loss?.statements_count) && !res.data?.balance_sheet) {
          setError('No statements found in the selected period.')
        }
      } else {
        setError(res.message || 'Failed to build cumulative report')
      }
    } catch (e) {
      setError(e.message || 'Failed to build cumulative report')
    } finally {
      setLoading(false)
    }
  }

  const doExport = async (kind) => {
    if (!report) return
    setExporting(kind)
    setError('')
    try {
      const suffix = `${report.start_date || 'inception'}_${report.end_date || 'to-date'}`
      if (kind === 'pdf') {
        await generateCumulativeReportPDF(report, {
          customerName: memberLabel,
          memberId: report.mem_no,
          companyName
        })
      } else if (kind === 'xlsx') {
        exportCumulativeReportToExcel(report, memberLabel, `cumulative_report_${suffix}.xlsx`)
      } else {
        exportCumulativeReportToCsv(report, memberLabel, `cumulative_report_${suffix}.csv`)
      }
    } catch (e) {
      setError('Export failed: ' + (e.message || 'Unknown error'))
    } finally {
      setExporting('')
    }
  }

  const pl = report?.profit_loss
  const totals = pl?.totals || {}
  const bs = report?.balance_sheet
  const inp = 'px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500'

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
        <BarChart3 className="h-5 w-5 text-blue-600" />
        <div>
          <h2 className="font-semibold text-gray-900">Cumulative Report</h2>
          <p className="text-xs text-gray-500">Totals across all statements in a date range. Leave dates empty for all-time figures.</p>
        </div>
      </div>

      <div className="p-5">
        {/* Controls */}
        <div className="flex flex-wrap items-end gap-3 mb-4">
          {isAdmin && !memberNo && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Member No</label>
              <input value={memberInput} onChange={e => setMemberInput(e.target.value)} placeholder="e.g. 10023" className={`${inp} w-36`} />
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className={inp} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className={inp} />
          </div>
          <button
            onClick={generate}
            disabled={loading}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
          >
            {loading
              ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
              : <Search className="h-4 w-4" />}
            {loading ? 'Generating…' : 'Generate'}
          </button>
          {report && (
            <div className="flex gap-2 ml-auto">
              <button onClick={() => doExport('pdf')} disabled={!!exporting}
                className="flex items-center gap-1 text-sm text-gray-600 border border-gray-200 px-3 py-2 rounded-lg hover:bg-gray-50 disabled:opacity-50">
                <Download className="h-3.5 w-3.5" /> PDF
              </button>
              <button onClick={() => doExport('xlsx')} disabled={!!exporting}
                className="flex items-center gap-1 text-sm text-gray-600 border border-gray-200 px-3 py-2 rounded-lg hover:bg-gray-50 disabled:opacity-50">
                <FileSpreadsheet className="h-3.5 w-3.5" /> XLSX
              </button>
              <button onClick={() => doExport('csv')} disabled={!!exporting}
                className="flex items-center gap-1 text-sm text-gray-600 border border-gray-200 px-3 py-2 rounded-lg hover:bg-gray-50 disabled:opacity-50">
                <FileText className="h-3.5 w-3.5" /> CSV
              </button>
            </div>
          )}
        </div>

        {error && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 mb-4 text-sm">
            <AlertCircle className="h-4 w-4 flex-shrink-0" /> {error}
          </div>
        )}

        {report && pl && (
          <div>
            <p className="text-xs text-gray-500 mb-3">
              {pl.statements_count} P&amp;L statement(s) from {report.start_date || 'inception'} to {report.end_date || 'date'}
              {bs ? ` · Balance sheet as at ${bs.as_of}` : ' · No balance sheet in range'}
            </p>

            {/* P&L summary tiles */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
              {[
                ['Total Revenue', totals.total_revenue, 'text-gray-900'],
                ['Total COGS', totals.total_cogs, 'text-red-600'],
                ['Gross Profit', totals.gross_profit, 'text-emerald-700'],
                ['Operating Expenses', totals.total_operating_expenses, 'text-red-600'],
                ['Other Income', totals.other_income, 'text-emerald-700'],
                ['Other Expenses', totals.other_expenses, 'text-red-600']
              ].map(([label, value, color]) => (
                <div key={label} className="bg-gray-50 rounded-xl p-3">
                  <div className="text-xs text-gray-500 mb-1">{label}</div>
                  <div className={`text-base font-semibold ${color}`}>{fmt(value)}</div>
                </div>
              ))}
            </div>

            <div className={`py-3 px-4 rounded-xl font-semibold mb-4 ${(totals.net_income || 0) >= 0 ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'}`}>
              <div className="flex justify-between">
                <span>Cumulative Net Profit</span>
                <span>{fmt(totals.net_income)}</span>
              </div>
            </div>

            {/* Product breakdown */}
            {(pl.products || []).length > 0 && (
              <div className="mb-4">
                <h4 className="font-semibold text-gray-800 text-sm mb-2">Revenue by Product</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border border-gray-100 rounded-lg overflow-hidden">
                    <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                      <tr>
                        <th className="px-3 py-2 text-left">Product</th>
                        <th className="px-3 py-2 text-right">Revenue</th>
                        <th className="px-3 py-2 text-right">COGS</th>
                        <th className="px-3 py-2 text-right">Gross Profit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pl.products.map((p, i) => (
                        <tr key={i} className="border-t border-gray-100">
                          <td className="px-3 py-2 font-medium">{p.product_name}</td>
                          <td className="px-3 py-2 text-right text-green-700">{fmt(p.revenue)}</td>
                          <td className="px-3 py-2 text-right text-red-600">{fmt(p.cost_of_goods_sold)}</td>
                          <td className="px-3 py-2 text-right text-blue-700">{fmt(p.gross_profit)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Expense breakdown */}
            {(pl.expenses || []).length > 0 && (
              <div className="mb-4">
                <h4 className="font-semibold text-gray-800 text-sm mb-2">Operating Expenses by Category</h4>
                <div className="space-y-1">
                  {pl.expenses.map((e, i) => (
                    <div key={i} className="flex justify-between text-sm text-gray-700">
                      <span>{e.category}</span>
                      <span className="text-red-600">{fmt(e.amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Balance sheet snapshot */}
            {bs?.totals && (
              <div>
                <h4 className="font-semibold text-gray-800 text-sm mb-2">Balance Sheet as at {bs.as_of}</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                  {[
                    ['Total Assets', bs.totals.total_assets, 'green'],
                    ['Total Liabilities', bs.totals.total_liabilities, 'red'],
                    ['Total Equity', bs.totals.total_equity, 'blue']
                  ].map(([label, value, color]) => (
                    <div key={label} className={`bg-${color}-50 border border-${color}-200 rounded-lg p-3`}>
                      <p className="text-xs text-gray-500">{label}</p>
                      <p className={`font-bold text-${color}-700`}>{fmt(value)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default CumulativeReportPanel
